import { Link, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import { layout, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../../src/components/button";
import { Screen } from "../../src/components/screen";
import { type Colors, useColors } from "../../src/theme";
import { trpc } from "../../src/trpc";

interface SessionSummary {
  totals: { correct: number; total: number };
  attempts: Array<{
    questionId: string;
    stem: string;
    chosen: string;
    correctLabel: string;
    isCorrect: boolean;
    explanation: string;
    topicSlug: string;
    topicLabel: string;
  }>;
  topics: Array<{
    slug: string;
    label: string;
    correct: number;
    total: number;
  }>;
  mastery: Array<{
    slug: string;
    label: string;
    pct: number;
    samples: number;
  }>;
}

export default function PracticeDone(): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const params = useLocalSearchParams<{ sessionId?: string }>();
  const sessionId = params.sessionId ?? null;
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMissed, setShowMissed] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError("No session id — refresh from the practice flow.");
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const res = await trpc.questions.sessionSummary.query({ sessionId });
        setSummary(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load summary.");
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.accentSoftText} />
      </View>
    );
  }

  if (error || !summary || summary.totals.total === 0) {
    return (
      <View style={styles.center}>
        <Text style={styles.title}>Practice complete</Text>
        <Text style={styles.errorText}>{error ?? "No attempts recorded."}</Text>
        <View style={styles.actions}>
          <Link href="/practice" asChild>
            <Button label="Practice again" fullWidth />
          </Link>
          <Link href="/" asChild>
            <Button label="Home" variant="secondary" fullWidth />
          </Link>
        </View>
      </View>
    );
  }

  const { correct, total } = summary.totals;
  const pct = Math.round((correct / total) * 100);
  const missed = summary.attempts.filter((a) => !a.isCorrect);
  const weakestTopic = summary.topics
    .filter((t) => t.total >= 2)
    .map((t) => ({ ...t, rate: t.correct / t.total }))
    .sort((a, b) => a.rate - b.rate)[0];

  return (
    <Screen>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scroll}>
      <View style={styles.card}>
        <Text style={styles.title}>Practice complete</Text>
        <Text style={styles.score}>
          {correct} / {total}
        </Text>
        <Text
          style={[
            styles.pct,
            { color: pct >= 80 ? colors.textCorrect : colors.heading },
          ]}
        >
          {pct}%
        </Text>
        {pct >= 80 ? (
          <Text style={styles.pctNote}>
            That's at or above the typical pass threshold. Keep this consistent
            across topics and you're ready.
          </Text>
        ) : pct >= 60 ? (
          <Text style={styles.pctNote}>
            Solid progress — focus on weakest topics to push past 80%.
          </Text>
        ) : (
          <Text style={styles.pctNote}>
            Below the typical pass threshold. Review missed questions and
            target your weakest topic.
          </Text>
        )}
      </View>

      {summary.topics.length > 0 ? (
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>By topic</Text>
          {summary.topics.map((t) => {
            const tPct = Math.round((t.correct / t.total) * 100);
            return (
              <View key={t.slug} style={styles.topicRow}>
                <Text style={styles.topicLabel} numberOfLines={1}>
                  {t.label}
                </Text>
                <Text style={styles.topicScore}>
                  {t.correct} / {t.total} ({tPct}%)
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}

      {missed.length > 0 ? (
        <View style={styles.card}>
          <Pressable onPress={() => setShowMissed((s) => !s)}>
            <Text style={styles.sectionTitle}>
              {missed.length} missed{" "}
              {missed.length === 1 ? "question" : "questions"}
              {showMissed ? " (hide)" : " (review)"}
            </Text>
          </Pressable>
          {showMissed
            ? missed.map((a) => (
                <View key={a.questionId} style={styles.missedRow}>
                  <Text style={styles.missedTopic}>{a.topicLabel}</Text>
                  <Text style={styles.missedStem}>{a.stem}</Text>
                  <Text style={styles.missedAnswer}>
                    You picked {a.chosen} · correct: {a.correctLabel}
                  </Text>
                  <Text style={styles.missedExplanation} numberOfLines={4}>
                    {a.explanation}
                  </Text>
                </View>
              ))
            : null}
        </View>
      ) : null}

      {summary.mastery.length > 0 ? (
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Current mastery</Text>
          {summary.mastery.map((m) => (
            <View key={m.slug} style={styles.topicRow}>
              <Text style={styles.topicLabel} numberOfLines={1}>
                {m.label}
              </Text>
              <Text style={styles.topicScore}>
                {m.pct}% ({m.samples} {m.samples === 1 ? "attempt" : "attempts"})
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      {weakestTopic ? (
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Suggested next</Text>
          <Text style={styles.suggestion}>
            Your weakest topic this session was{" "}
            <Text style={styles.suggestionEmph}>{weakestTopic.label}</Text>{" "}
            ({weakestTopic.correct}/{weakestTopic.total}). The next batch will
            focus there.
          </Text>
        </View>
      ) : null}

      <View style={styles.actions}>
        <Link href="/practice" asChild>
          <Button label="Practice again" fullWidth />
        </Link>
        <Link href="/" asChild>
          <Button label="Home" variant="secondary" fullWidth />
        </Link>
      </View>
      </ScrollView>
    </Screen>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  scrollView: { flex: 1 },
  scroll: {
    padding: space[4],
    gap: space[3],
    alignItems: "center",
  },
  center: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
    gap: space[3],
  },
  card: {
    width: "100%",
    maxWidth: layout.formMaxWidth,
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space[4],
    borderWidth: 1,
    borderColor: colors.border,
    gap: space[2],
  },
  title: { ...textStyle.h2, color: colors.heading, textAlign: "center" },
  score: {
    ...textStyle.h1,
    fontSize: 48,
    lineHeight: 54,
    color: colors.textPrimary,
    textAlign: "center",
  },
  pct: { ...textStyle.h3, textAlign: "center" },
  pctNote: {
    ...textStyle.bodySmall,
    color: colors.textSecondary,
    textAlign: "center",
    marginTop: space[1],
  },
  sectionTitle: { ...textStyle.label, color: colors.textPrimary },
  topicRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: space[1],
  },
  topicLabel: { ...textStyle.body, color: colors.textPrimary, flex: 1 },
  topicScore: { ...textStyle.body, color: colors.textSecondary },
  missedRow: {
    paddingVertical: space[2],
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: space[1],
  },
  missedTopic: {
    ...textStyle.bodySmall,
    color: colors.textMuted,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  missedStem: { ...textStyle.body, color: colors.textPrimary },
  missedAnswer: { ...textStyle.bodySmall, color: colors.textWrong },
  missedExplanation: {
    ...textStyle.bodySmall,
    color: colors.textSecondary,
  },
  suggestion: { ...textStyle.body, color: colors.textPrimary },
  suggestionEmph: { fontWeight: "600", color: colors.accentSoftText },
  actions: {
    width: "100%",
    maxWidth: layout.formMaxWidth,
    marginTop: space[3],
    gap: space[2],
  },
  errorText: {
    ...textStyle.body,
    color: colors.textWrong,
    textAlign: "center",
  },
  });
}
